import { Button, Flex, FlexItem } from "@wordpress/components";
import { useSelect, withSelect } from "@wordpress/data";
import { dateI18n, __experimentalGetSettings } from "@wordpress/date";
import { Fragment, useMemo } from "@wordpress/element";
import { EditProps } from "blocks/types";
import classnames from "classnames";
import React from "react";

import { useBlockStyle } from "../../hooks";

import { useClientId, useCopyPasteStyles, useDeviceType } from "@blocks/hooks";

import { Icon } from "../../components";
import "../assets/sass/blocks/featured-posts/style.scss";
import InspectorControls from "./components/InspectorControls";
import { cn } from "@blocks/utils";

import { upperFirst } from "lodash";

interface Props extends EditProps<any> {
	categories: Array<any>;
	posts: Array<any>;
	tags: Array<any>;
	numberOfPosts: number;
	page: number;
	author: Array<any>;
}

const Edit: React.ComponentType<Props> = (props) => {
	const {
		posts,
		className,
		attributes: {
			layout,
			layout1AdvancedStyle,
			layout2AdvancedStyle,
			layout3AdvancedStyle,
			layout4AdvancedStyle,
			layout5AdvancedStyle,
			layout6AdvancedStyle,
			column,
			label,
			category,
			postCount,
			postBoxStyle,

			enableHeading,
			headingLayout,
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingLayout6AdvancedStyle,
			headingLayout7AdvancedStyle,
			headingLayout8AdvancedStyle,
			headingLayout9AdvancedStyle,

			enableFeaturedImage,
			hoverAnimation,

			postTitleMarkup,

			enableCategory,
			enableHighlightedCategory,
			enableComment,
			enableHighlightedComment,

			metaPosition,
			enableAuthor,
			enableDate,
			enableReadTime,
			enableViewCount,
			enableIcon,
			separatorType,
			enableMetaSeparator,

			enableExcerpt,
			excerptLimit,

			enableReadMore,
			readMoreText,
			enableReadMoreIcon,
			readMoreIcon,

			enableViewMore,
			viewMoreText,
			enableViewMoreIcon,
			viewMoreIcon,
			viewMoreLink,
			viewButtonPosition,

			size,

			enablePagination,
			hideOnDesktop,

			layout4TopRowCount,
			layout4BottomRowCount,

			postCardBorder,
			enableCategoryBorder,
			enableHighlightedCategoryBorder,
			categoryPosition,
			highlightedCategoryPosition,

			enableReadMoreBorder,
			enableViewMoreBorder,
		},
		setAttributes,
		categories,
		tags,
		numberOfPosts,
		page,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();
	const { Style } = useBlockStyle({
		blockName: "featured-posts",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	// @ts-ignore
	const authors = useSelect((select) => {
		const { getUsers } = select("core") as any;
		return getUsers({ per_page: -1 });
	}) as any[];

	const authorOptions = authors
		? authors.map((author) => ({
				value: author?.id.toString(),
				label: author?.name,
		  }))
		: [];

	const postTypeOptions = useSelect((select) => {
		const { getPostTypes } = select("core") as any;
		const { getCurrentPost } = select("core/editor") as any;

		const allPostTypes = getPostTypes({ per_page: -1 }) || [];
		const post = getCurrentPost();
		const currentPostType = post?.type;
		const meta = post?.meta?._mzb_template;

		if (currentPostType === "mzb-builder-template") {
			return allPostTypes
				.filter((type: any) =>
					["post", "mzb-builder"].includes(type.slug)
				)
				.map((type: any) => {
					if (type.slug === "mzb-builder" && meta) {
						return {
							value: type.slug,
							label: `${upperFirst(meta)} Builder`,
						};
					}
					return {
						value: type.slug,
						label: type.name || type.slug,
					};
				});
		}

		return allPostTypes
			.filter((type: any) => type.slug !== "mzb-builder-template")
			.map((type: any) => ({
				value: type.slug,
				label: type.name || type.slug,
			}));
	}, []);

	const currentCategoryIndex = useMemo(() => {
		return categories
			? categories.findIndex((cat) => cat.id === parseInt(category))
			: -1; // Use -1 or another appropriate default value
	}, [category, categories]);

	const classNames = classnames(
		`mzb-featured-posts mzb-featured-posts-${clientId}`,
		size && `is-${size}`,
		className,
		hideOnDesktop && "magazine-blocks-hide-on-desktop",
		enableViewMoreBorder ? "mzb-view-more-border" : ""
	);

	const classNames2 = classnames(
		`mzb-posts mzb-${layout} ${
			layout === `layout-1` || layout === `layout-4`
				? `mzb-post-col--${column || "1"}`
				: ""
		} mzb-${postBoxStyle} ${postCount === 1 ? "mzb-post-col--full" : ""}`,
		{
			[`mzb-${layout1AdvancedStyle}`]: layout === `layout-1`,
			[`mzb-${layout2AdvancedStyle}`]: layout === `layout-2`,
			[`mzb-${layout3AdvancedStyle}`]: layout === `layout-3`,
			[`mzb-${layout4AdvancedStyle} mzb-layout-4-style-1-top-row-${layout4TopRowCount} mzb-layout-4-style-1-bottom-row-${layout4BottomRowCount}`]:
				layout === `layout-4`,
			[`mzb-${layout5AdvancedStyle}`]: layout === `layout-5`,
			[`mzb-${layout6AdvancedStyle}`]: layout === `layout-6`,
		},
		{
			[`mzb-post-card-border`]: postCardBorder?.border,
		},
		{
			[`mzb-read-more-border`]: enableReadMoreBorder,
		}
	);

	const classNames3 = classnames(`mzb-post-heading mzb-${headingLayout}`, {
		[`mzb-${headingLayout1AdvancedStyle}`]:
			headingLayout === `heading-layout-1`,
		[`mzb-${headingLayout2AdvancedStyle}`]:
			headingLayout === `heading-layout-2`,
		[`mzb-${headingLayout3AdvancedStyle}`]:
			headingLayout === `heading-layout-3`,
		[`mzb-${headingLayout4AdvancedStyle}`]:
			headingLayout === `heading-layout-4`,
		[`mzb-${headingLayout5AdvancedStyle}`]:
			headingLayout === `heading-layout-5`,
		[`mzb-${headingLayout6AdvancedStyle}`]:
			headingLayout === `heading-layout-6`,
		[`mzb-${headingLayout7AdvancedStyle}`]:
			headingLayout === `heading-layout-7`,
		[`mzb-${headingLayout8AdvancedStyle}`]:
			headingLayout === `heading-layout-8`,
		[`mzb-${headingLayout9AdvancedStyle}`]:
			headingLayout === `heading-layout-9`,
	});

	const NumberedPagination = ({
		totalPages,
		currentPage,
		onPageChange,
	}: {
		totalPages: number;
		currentPage: number;
		onPageChange: (val: number) => void;
	}) => {
		if (totalPages < 2) {
			return null; // Don't render pagination if there's only one page
		}

		const pages = Array.from(
			{ length: totalPages },
			(_, index) => index + 1
		);

		return (
			<Flex>
				{pages.map((page) => (
					<FlexItem key={page}>
						<Button
							isPrimary={page === currentPage}
							onClick={() => onPageChange(page)}
						>
							{page}
						</Button>
					</FlexItem>
				))}
			</Flex>
		);
	};

	// Fetch the next page of posts
	const loadMorePosts = (nextPage: number) => {
		// Update the block's attributes to trigger a re-render with the next page
		setAttributes({ page: nextPage });
	};

	// Render numbered pagination
	const pagination = (
		<NumberedPagination
			totalPages={Math.ceil(numberOfPosts / postCount)}
			currentPage={page}
			onPageChange={loadMorePosts}
		/>
	);

	return (
		<Fragment>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={setAttributes}
				categories={categories}
				tags={tags}
				authorOptions={authorOptions}
				postTypeOptions={postTypeOptions}
			/>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<div className={classNames}>
				<div className={classNames3}>
					{enableHeading === true && (
						<h2 className="mzb-heading-text">{label}</h2>
					)}
					{viewButtonPosition === "top" && enableViewMore && (
						<div className="mzb-view-more">
							{/*eslint-disable-next-line react/jsx-no-target-blank */}
							<a
								href={viewMoreLink?.url}
								target={
									viewMoreLink?.newTab ? "_blank" : "_self"
								}
								rel={[
									viewMoreLink?.newTab
										? "noreferrer noopener"
										: "",
									viewMoreLink?.noFollow ? "nofollow" : "",
								]
									.filter(Boolean)
									.join(" ")}
							>
								<p>{viewMoreText}</p>
								{enableViewMoreIcon && (
									<Icon
										type="frontendIcon"
										name={viewMoreIcon}
										size={24}
									/>
								)}
							</a>
						</div>
					)}
				</div>
				{
					<div className={classNames2}>
						<Fragment>
							{(posts || []).map((post, idx) => {
								const maxWords = excerptLimit; // Replace with your desired word limit
								const excerpt = post?.excerpt?.rendered
									.split(" ")
									.slice(0, maxWords)
									.join(" ");

								// @ts-ignore
								return (
									<div
										className={`mzb-post ${
											layout !== "layout-4" &&
											(0 === idx ||
												(layout === "layout-1" &&
													((2 ===
														parseInt(
															column || "1"
														) &&
														(0 === idx ||
															1 === idx)) ||
														(3 ===
															parseInt(
																column || "1"
															) &&
															(0 === idx ||
																1 === idx ||
																2 === idx)))))
												? `mzb-first-post--highlight ${
														enableHighlightedCategoryBorder
															? "mzb-highlighted-post-category-border"
															: ""
												  }`
												: `${
														enableCategoryBorder
															? "mzb-post-category-border"
															: ""
												  }`
										}`}
										key={idx}
									>
										{post
											?.magazine_blocks_featured_image_url
											?.full?.[0] &&
											enableFeaturedImage && (
												<div
													className={`mzb-featured-image ${hoverAnimation}
													${
														layout !== "layout-4" &&
														(0 === idx ||
															(layout ===
																"layout-1" &&
																((2 ===
																	parseInt(
																		column ||
																			"1"
																	) &&
																	(0 ===
																		idx ||
																		1 ===
																			idx)) ||
																	(3 ===
																		parseInt(
																			column ||
																				"1"
																		) &&
																		(0 ===
																			idx ||
																			1 ===
																				idx ||
																			2 ===
																				idx)))))
															? highlightedCategoryPosition ===
															  "in-image"
																? "mzb-category--inside-image"
																: ""
															: categoryPosition ===
															  "in-image"
															? "mzb-category--inside-image"
															: ""
													}`}
												>
													<img
														src={
															post
																?.magazine_blocks_featured_image_url
																?.full[0]
														}
													/>
													{layout !== "layout-4" &&
													(0 === idx ||
														(layout ===
															"layout-1" &&
															((2 ===
																parseInt(
																	column ||
																		"1"
																) &&
																(0 === idx ||
																	1 ===
																		idx)) ||
																(3 ===
																	parseInt(
																		column ||
																			"1"
																	) &&
																	(0 ===
																		idx ||
																		1 ===
																			idx ||
																		2 ===
																			idx)))))
														? (enableHighlightedCategory ||
																enableHighlightedComment) &&
														  highlightedCategoryPosition ===
																"in-image" && (
																<div className="mzb-post-meta">
																	{enableHighlightedCategory && (
																		<span
																			className="mzb-post-categories"
																			dangerouslySetInnerHTML={{
																				__html: post.magazine_blocks_category,
																			}}
																		/>
																	)}
																	{enableHighlightedComment && (
																		<span className="comments-link">
																			<Icon
																				type="frontendIcon"
																				name="commentIcon"
																				size={
																					24
																				}
																			/>{" "}
																			{
																				post.magazine_blocks_comment
																			}
																		</span>
																	)}
																</div>
														  )
														: (enableCategory ||
																enableComment) &&
														  categoryPosition ===
																"in-image" && (
																<div className="mzb-post-meta">
																	{enableCategory && (
																		<span
																			className="mzb-post-categories"
																			dangerouslySetInnerHTML={{
																				__html: post.magazine_blocks_category,
																			}}
																		/>
																	)}
																	{enableComment && (
																		<span className="comments-link">
																			<Icon
																				type="frontendIcon"
																				name="commentIcon"
																				size={
																					24
																				}
																			/>{" "}
																			{
																				post.magazine_blocks_comment
																			}
																		</span>
																	)}
																</div>
														  )}
												</div>
											)}
										<div
											className={`mzb-post-content ${
												metaPosition
													? `mzb-meta-position--${metaPosition}`
													: ""
											}`}
										>
											{layout !== "layout-4" &&
											(0 === idx ||
												(layout === "layout-1" &&
													((2 ===
														parseInt(
															column || "1"
														) &&
														(0 === idx ||
															1 === idx)) ||
														(3 ===
															parseInt(
																column || "1"
															) &&
															(0 === idx ||
																1 === idx ||
																2 === idx)))))
												? (enableHighlightedCategory ||
														enableHighlightedComment) &&
												  highlightedCategoryPosition ===
														"out-image" && (
														<div className="mzb-post-meta">
															{enableHighlightedCategory && (
																<span
																	className="mzb-post-categories"
																	dangerouslySetInnerHTML={{
																		__html: post?.magazine_blocks_category,
																	}}
																/>
															)}
															{enableHighlightedComment && (
																<span className="comments-link">
																	<Icon
																		type="frontendIcon"
																		name="commentIcon"
																		size={
																			24
																		}
																	/>{" "}
																	{
																		post?.magazine_blocks_comment
																	}
																</span>
															)}
														</div>
												  )
												: (enableCategory ||
														enableComment) &&
												  categoryPosition ===
														"out-image" && (
														<div className="mzb-post-meta">
															{enableCategory && (
																<span
																	className="mzb-post-categories"
																	dangerouslySetInnerHTML={{
																		__html: post?.magazine_blocks_category,
																	}}
																/>
															)}
															{enableComment && (
																<span className="comments-link">
																	<Icon
																		type="frontendIcon"
																		name="commentIcon"
																		size={
																			24
																		}
																	/>{" "}
																	{
																		post?.magazine_blocks_comment
																	}
																</span>
															)}
														</div>
												  )}

											{(enableAuthor ||
												enableDate ||
												enableReadTime ||
												enableViewCount) &&
												"top" === metaPosition && (
													<div
														className={cn(
															"mzb-post-entry-meta",
															enableMetaSeparator &&
																separatorType &&
																`mzb-meta-separator--${separatorType}`
														)}
													>
														{enableAuthor && (
															<span className="mzb-post-author">
																{enableIcon ===
																	true && (
																	<img
																		className="post-author-image"
																		src={
																			post?.magazine_blocks_author_image
																		}
																	/>
																)}
																<a href="#">
																	{" "}
																	{
																		post
																			?.magazine_blocks_author
																			?.display_name
																	}{" "}
																</a>
															</span>
														)}

														{enableDate && (
															<span className="mzb-post-date">
																{enableIcon ===
																	true && (
																	<Icon
																		type="blockIcon"
																		name="calendar"
																		size={
																			24
																		}
																	/>
																)}
																<a href="#">
																	{" "}
																	{dateI18n(
																		__experimentalGetSettings()
																			.formats
																			.date,
																		post?.date_gmt,
																		undefined
																	)}{" "}
																</a>
															</span>
														)}

														{enableReadTime ===
															true && (
															<span className="mzb-post-read-time">
																{enableIcon ===
																	true && (
																	<Icon name="clock" />
																)}
																<span>
																	{
																		post?.magazineBlocksPostReadTime
																	}{" "}
																	min read
																</span>
															</span>
														)}
														{enableViewCount ===
															true && (
															<span className="mzb-post-view-count">
																{enableIcon ===
																	true && (
																	<Icon
																		name="eyeO"
																		fill="#fff"
																	/>
																)}
																<span>
																	{post?.magazineBlocksPostViewCount ||
																		0}{" "}
																	views
																</span>
															</span>
														)}
													</div>
												)}

											<>
												{React.createElement(
													postTitleMarkup,
													{
														className:
															"mzb-post-title",
													},
													<a href="#">
														{post?.title?.rendered}
													</a>
												)}
											</>

											{(enableAuthor ||
												enableDate ||
												enableReadTime ||
												enableViewCount) &&
												"bottom" === metaPosition && (
													<div
														className={cn(
															"mzb-post-entry-meta",
															enableMetaSeparator &&
																separatorType &&
																`mzb-meta-separator--${separatorType}`
														)}
													>
														{enableAuthor && (
															<span className="mzb-post-author">
																{enableIcon ===
																	true && (
																	<img
																		className="post-author-image"
																		src={
																			post?.magazine_blocks_author_image
																		}
																	/>
																)}
																<a href="#">
																	{" "}
																	{
																		post
																			?.magazine_blocks_author
																			?.display_name
																	}{" "}
																</a>
															</span>
														)}

														{enableDate && (
															<span className="mzb-post-date">
																{enableIcon ===
																	true && (
																	<Icon
																		type="blockIcon"
																		name="calendar"
																		size={
																			24
																		}
																	/>
																)}
																<a href="#">
																	{" "}
																	{dateI18n(
																		__experimentalGetSettings()
																			.formats
																			.date,
																		post?.date_gmt,
																		undefined
																	)}{" "}
																</a>
															</span>
														)}

														{enableReadTime && (
															<span className="mzb-post-read-time">
																{enableIcon ===
																	true && (
																	<Icon name="clock" />
																)}
																<span>
																	{
																		post?.magazineBlocksPostReadTime
																	}{" "}
																	min read
																</span>
															</span>
														)}
														{enableViewCount && (
															<span className="mzb-post-view-count">
																{enableIcon ===
																	true && (
																	<Icon
																		name="eyeO"
																		fill="#fff"
																	/>
																)}
																<span>
																	{post?.magazineBlocksPostViewCount ||
																		0}{" "}
																	views
																</span>
															</span>
														)}
													</div>
												)}
											{(enableExcerpt ||
												enableReadMore) &&
												(layout !== "layout-6" ||
													idx === 0) &&
												(layout !== "layout-5" ||
													idx === 0) && (
													<div className="mzb-entry-content">
														{enableExcerpt && (
															<div
																className="mzb-entry-summary"
																dangerouslySetInnerHTML={{
																	__html: excerpt,
																}}
															/>
														)}
														{enableReadMore && (
															<div className="mzb-read-more">
																<a href="#">
																	{
																		readMoreText
																	}
																	{enableReadMoreIcon && (
																		<Icon
																			type="frontendIcon"
																			name={
																				readMoreIcon
																			}
																			size={
																				24
																			}
																		/>
																	)}
																</a>
															</div>
														)}
													</div>
												)}
										</div>
									</div>
								);
							})}
						</Fragment>
					</div>
				}
				{viewButtonPosition === "bottom" && enableViewMore && (
					<div className="mzb-view-more">
						{/*eslint-disable-next-line react/jsx-no-target-blank */}
						<a
							href={viewMoreLink?.url}
							target={viewMoreLink?.newTab ? "_blank" : "_self"}
							rel={[
								viewMoreLink?.newTab
									? "noreferrer noopener"
									: "",
								viewMoreLink?.noFollow ? "nofollow" : "",
							]
								.filter(Boolean)
								.join(" ")}
						>
							<p>{viewMoreText}</p>
							{enableViewMoreIcon && (
								<Icon
									type="frontendIcon"
									name={viewMoreIcon}
									size={24}
								/>
							)}
						</a>
					</div>
				)}
				{enablePagination && (
					<div className="mzb-pagination-numbers">{pagination}</div>
				)}
			</div>
		</Fragment>
	);
};

// @ts-ignore
export default withSelect((select, props) => {
	const { getEntityRecords } = select("core");
	const { getCurrentPost } = select("core/editor");

	const {
		attributes: {
			category,
			tag,
			postCount,
			offset,
			order,
			orderBy,
			orderType,
			authorName,
			excludedCategory,
			postType,
		},
	} = props;

	const post = getCurrentPost();
	const isTemplate = post?.type === "mzb-builder-template";
	const isArchive = post?.type === "archive";
	const isSearch = post?.type === "search";

	const selectedPostType = isTemplate ? "post" : postType || "post";

	const shouldSkipFilters = isTemplate && (isArchive || isSearch);

	const query: any = {
		per_page: postCount,
		offset: offset || 0,
		orderby: orderBy,
		order: orderType,
		author: authorName,
	};

	if (!shouldSkipFilters) {
		if (category && category !== "all") {
			query.categories = parseInt(category);
		}
		if (tag && tag !== "all") {
			query.tags = tag;
		}
		if (excludedCategory && excludedCategory !== "") {
			query.categories_exclude = excludedCategory;
		}
	}

	return {
		posts: getEntityRecords("postType", selectedPostType, query),
		numberOfPosts:
			getEntityRecords("postType", selectedPostType, {
				per_page: -1,
				categories:
					"all" === category || selectedPostType !== "post"
						? undefined
						: parseInt(category),
			})?.length || 0,
		categories:
			getEntityRecords("taxonomy", "category", { per_page: -1 }) || [],
		tags: getEntityRecords("taxonomy", "post_tag", { per_page: -1 }) || [],
	};
})(Edit);
